/**
 * 지난 회의 관리 패널(관리자) — §11 아카이브.
 *
 * `RoomAdminPanel` 과 다르다: 지난 회의는 **끝난 회의**라 상태가 더 이상
 * 변하지 않는다. 그래서 여기는 폴링이 없다 — 마운트 시 한 번만 읽는다.
 * 목적도 다르다: 저쪽은 "지금 뭘 닫을까" 이고 여기는 "끝난 회의록을
 * 무엇이었는지 확인" 이다.
 */

import { useEffect, useState } from 'react'
import { Button } from '@cupia/ui/button'
import { Card, CardContent } from '@cupia/ui/card'
import { Spinner } from '@cupia/ui/spinner'
import { Empty } from '@cupia/ui/empty'
import { Archive, KeyRound } from 'lucide-react'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@cupia/ui/drawer'
import { listMeetings, fetchMeetingTranscript, type MeetingRow } from '../lib/meetings-admin'
import { formatStamp } from '../lib/admin'
import { EXPORT_FORMATS, FORMAT_LABELS, saveBlob, type ExportFormat, type TranscriptFile } from '../lib/export'
import { ApiError } from '../lib/api'

export interface MeetingsAdminPanelProps {
  onNotify?: (message: string) => void
}

export function MeetingsAdminPanel({ onNotify }: MeetingsAdminPanelProps): React.ReactElement {
  const [meetings, setMeetings] = useState<MeetingRow[] | null>(null)
  const [denied, setDenied] = useState<string | null>(null)
  const [selected, setSelected] = useState<MeetingRow | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const res = await listMeetings()
        setMeetings(Array.isArray(res?.meetings) ? res.meetings : [])
      } catch (e) {
        if (e instanceof ApiError && (e.status === 403 || e.status === 401)) {
          setDenied(e.message)
          setMeetings([])
          return
        }
        setMeetings([])
        onNotify?.('지난 회의 목록을 불러오지 못했습니다.')
      }
    })()
  }, [onNotify])

  if (meetings === null) {
    return (
      <div className="flex items-center gap-2 p-4 text-[var(--color-muted-foreground)]">
        <Spinner className="h-4 w-4" />
        지난 회의 목록을 불러오는 중…
      </div>
    )
  }

  if (denied) {
    return (
      <Card>
        <CardContent className="p-4">
          <Empty
            icon={<KeyRound className="h-6 w-6" />}
            title="지난 회의를 볼 권한이 없습니다"
            description={denied}
          />
        </CardContent>
      </Card>
    )
  }

  if (meetings.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <Empty icon={<Archive className="h-6 w-6" />} title="지난 회의가 없습니다" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center gap-2 p-4">
          <span className="font-medium">지난 회의</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-start text-(length:--text-sm-size)" data-testid="admin-meetings">
            <thead className="border-b border-[var(--color-border)] text-[var(--color-muted-foreground)]">
              <tr>
                <th className="px-4 py-2.5 text-start font-medium">회의 코드</th>
                <th className="px-4 py-2.5 text-start font-medium">방향</th>
                <th className="px-4 py-2.5 text-start font-medium">종료 시각</th>
                <th className="px-4 py-2.5 text-start font-medium">자막/대화</th>
                <th className="px-4 py-2.5 text-start font-medium">보존 기간</th>
                <th className="px-4 py-2.5 text-end font-medium">조치</th>
              </tr>
            </thead>
            <tbody>
              {meetings.map((row) => (
                <tr
                  key={row.room}
                  data-testid={`meeting-row-${row.room}`}
                  className="border-b border-[var(--color-border)] last:border-0"
                >
                  <td className="px-4 py-2.5 font-medium">{row.room}</td>
                  <td className="px-4 py-2.5">
                    {row.src} → {row.dst}
                  </td>
                  <td className="px-4 py-2.5 text-[var(--color-muted-foreground)]">
                    {formatStamp(row.ended_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    {row.captions} / {row.chats}
                  </td>
                  <td className="px-4 py-2.5">{row.retention_days}일</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        data-testid={`meeting-view-${row.room}`}
                        onClick={() => setSelected(row)}
                      >
                        회의록 보기
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
      <MeetingTranscriptDrawer
        meeting={selected}
        onOpenChange={(open) => {
          if (!open) setSelected(null)
        }}
      />
    </Card>
  )
}

/**
 * 지난 회의의 회의록 뷰어 — `summary-drawer.tsx` 의 드로어 형태를 그대로
 * 따른다. 여기서 여는 회의는 참가하지 않았을 수 있으므로 서버가 열람을
 * 감사에 남긴다(`fetchMeetingTranscript` 참조) — 화면은 그 사실을 숨기지
 * 않고 그저 회의록을 보여 준다.
 */
function MeetingTranscriptDrawer({
  meeting,
  onOpenChange,
}: {
  meeting: MeetingRow | null
  onOpenChange: (open: boolean) => void
}): React.ReactElement {
  const [fmt, setFmt] = useState<ExportFormat>('txt')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [text, setText] = useState('')
  const [file, setFile] = useState<TranscriptFile | null>(null)

  const open = meeting !== null

  useEffect(() => {
    if (!meeting) return
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const f = await fetchMeetingTranscript(meeting.room, fmt)
        setFile(f)
        setText(await f.blob.text())
      } catch (e) {
        setError(e instanceof ApiError ? e.message : '회의록을 받지 못했습니다.')
        setFile(null)
        setText('')
      } finally {
        setLoading(false)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting?.room, fmt])

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]" data-testid="meeting-transcript-drawer">
        <DrawerHeader>
          <DrawerTitle>{meeting?.room} 회의록</DrawerTitle>
          <DrawerDescription>
            {meeting?.src} → {meeting?.dst}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex flex-wrap items-center gap-2 px-4 pb-2">
          {EXPORT_FORMATS.map((f) => (
            <Button
              key={f}
              size="sm"
              variant={fmt === f ? 'secondary' : 'ghost'}
              onClick={() => setFmt(f)}
            >
              {FORMAT_LABELS[f]}
            </Button>
          ))}
          <Button
            className="ms-auto"
            size="sm"
            disabled={!file}
            data-testid="meeting-download"
            onClick={() => file && saveBlob(file.blob, file.filename)}
          >
            내려받기
          </Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {loading && (
            <div className="flex items-center gap-2 text-(length:--text-sm-size) text-muted-foreground">
              <Spinner className="size-4" />
              불러오는 중…
            </div>
          )}

          {!loading && error && (
            <p
              className="text-(length:--text-sm-size) text-destructive"
              data-testid="meeting-transcript-error"
            >
              {error}
            </p>
          )}

          {!loading && !error && file && (text.trim() === '' || file.count === 0) && (
            <p className="text-(length:--text-sm-size) text-muted-foreground">
              자막이 없습니다.
            </p>
          )}

          {!loading && !error && file && text.trim() !== '' && file.count !== 0 && (
            <pre
              className="whitespace-pre-wrap font-mono text-(length:--text-sm-size) leading-relaxed"
              data-testid="meeting-transcript-body"
            >
              {text}
            </pre>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
